import type { ReactNode } from 'react'
import { createPortal } from 'react-dom'
import { AlertTriangle } from 'lucide-react'
import { useBodyScrollLock } from '@/hooks/useBodyScrollLock'

export interface CancelReimbursementDangerDialogShellProps {
  isOpen: boolean
  title: string
  children: ReactNode
  footer: ReactNode
  panelDataTestId?: string
}

/** Red-header / gray-footer chrome from the former DELETE confirm modal. */
export function CancelReimbursementDangerDialogShell({
  isOpen,
  title,
  children,
  footer,
  panelDataTestId,
}: CancelReimbursementDangerDialogShellProps) {
  useBodyScrollLock(isOpen)

  if (!isOpen) return null

  return createPortal(
    <div className="fixed inset-0 z-[10001] flex items-center justify-center bg-black/50 p-4">
      <div
        className="w-full max-w-2xl overflow-hidden rounded-xl bg-white shadow-2xl"
        role="dialog"
        aria-modal="true"
        aria-labelledby="cancel-reimbursement-title"
        data-testid={panelDataTestId}
      >
        <div className="flex items-center gap-3 bg-red-600 px-5 py-4">
          <AlertTriangle size={22} className="text-white" strokeWidth={2.5} aria-hidden="true" />
          <h3 id="cancel-reimbursement-title" className="text-lg font-semibold text-white">
            {title}
          </h3>
        </div>

        <div className="px-5 py-4">{children}</div>

        <div className="flex justify-end gap-2 border-t border-gray-200 bg-gray-50 px-5 py-3">
          {footer}
        </div>
      </div>
    </div>,
    document.body,
  )
}
